import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authApi } from '../api/authApi';
import { getRoleFromToken, getRedirectPath } from '../utils/jwtUtils';
import ToastContainer from '../Components/ToastContainer';
import './SignIn.css';
import { useSelector } from "react-redux";
import { getIsApprovedByAdmin } from '../api/deviceAuthApi';

const CAPTCHA_LENGTH = 6;
const CAPTCHA_CHAR_POOL = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const generateCaptchaText = () => {
  const characters = [];
  for (let i = 0; i < CAPTCHA_LENGTH; i += 1) {
    const index = Math.floor(Math.random() * CAPTCHA_CHAR_POOL.length);
    characters.push(CAPTCHA_CHAR_POOL[index]);
  }
  return characters.join('');
};

const drawCaptcha = (canvas, text) => {
  if (!canvas) {
    return;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }

  const width = 180;
  const height = 60;
  canvas.width = width;
  canvas.height = height;

  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#1f1040');
  gradient.addColorStop(1, '#2c1f5f');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  const charWidth = width / (text.length + 1);

  // Draw random lines for noise
  for (let i = 0; i < 5; i += 1) {
    ctx.strokeStyle = `rgba(255,255,255,${0.2 + Math.random() * 0.3})`;
    ctx.lineWidth = 1 + Math.random() * 1.5;
    ctx.beginPath();
    ctx.moveTo(Math.random() * width, Math.random() * height);
    ctx.lineTo(Math.random() * width, Math.random() * height);
    ctx.stroke();
  }

  // Draw characters
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const fontSize = 26 + Math.random() * 10;
    ctx.font = `${fontSize}px 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif`;
    const angle = (Math.random() - 0.5) * (Math.PI / 6);
    const x = charWidth * (i + 1);
    const y = height / 2 + (Math.random() - 0.5) * 10;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = `rgba(${150 + Math.floor(Math.random() * 100)}, ${130 + Math.floor(Math.random() * 100)}, 255, 0.9)`;
    ctx.shadowColor = 'rgba(236, 72, 153, 0.6)';
    ctx.shadowBlur = 3;
    ctx.fillText(char, 0, 0);
    ctx.restore();
  }

  // Add noise dots
  for (let i = 0; i < 60; i += 1) {
    ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.4})`;
    ctx.beginPath();
    ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
};

const CaptchaCanvas = ({ value, onRefresh }) => {
  const canvasRef = useRef(null);
  const refreshHandler = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  React.useEffect(() => {
    drawCaptcha(canvasRef.current, value);
  }, [value]);

  return (
    <div className="captcha-container">
      <canvas
        ref={canvasRef}
        className="captcha-canvas"
        width="180"
        height="60"
        role="img"
        aria-label="Distorted text captcha"
      />
      <button
        type="button"
        className="captcha-refresh"
        onClick={refreshHandler}
        aria-label="Refresh captcha"
      >
        ↻
      </button>
    </div>
  );
};

const SignIn = () => {
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state.deviceAuth.userEmail);
  const [signInMethod, setSignInMethod] = useState('username'); // 'username' or 'conferenceRoom'
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    otp: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  const [showDevOtp, setShowDevOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const refreshCaptcha = useCallback(() => {
    const nextValue = generateCaptchaText();
    setCaptchaValue(nextValue);
    setCaptchaInput('');
  }, []);

  React.useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  // OTP Timer countdown
  // React.useEffect(() => {
  //   if (otpTimer > 0) {
  //     const timerId = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
  //     return () => clearTimeout(timerId);
  //   } else if (otpTimer === 0 && otpSent) {
  //     setCanResendOtp(true);
  //   }
  // }, [otpTimer, otpSent]);

  // Check if user was logged out due to idle timeout
  React.useEffect(() => {
    const wasIdleLogout = sessionStorage.getItem('idleLogout');
    if (wasIdleLogout === 'true') {
      sessionStorage.removeItem('idleLogout');
      toast.info('You have been logged out due to inactivity.', { autoClose: 5000 });
    }
  }, []);

  // Check if user is already authenticated and device approval
  React.useEffect(() => {
    // Skip device auth check in development or if device auth service is not available
    const skipDeviceAuth = process.env.REACT_APP_SKIP_DEVICE_AUTH === 'true' ||
                           !process.env.REACT_APP_API_BASE_URL ||
                           process.env.NODE_ENV === 'development';

    // if (!skipDeviceAuth && !userEmail) {
    //   window.location.href = "/";
    //   return;
    // }

    // Check if user is approved by admin (only if device auth is enabled)
    const checkUserApproval = async (email) => {
      try {
        let response = await getIsApprovedByAdmin(email);
        if (!response?.data?.appovedByAdmin) {
          console.log("User not approved by admin, redirecting to home.");
          window.location.href = "/";
          return;
        } else {
          console.log("User is approved by admin.");
        }
      } catch (error) {
        console.log("ERROR WHILE FETCHING USER APPROVAL STATUS");
      }
    };

    if (!skipDeviceAuth && userEmail) {
      checkUserApproval(userEmail);
    }

    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      try {
        // Extract claims from token
        const claims = getRoleFromToken(token);
        const userRole = claims?.role?.toUpperCase();
        const tokenEmail = claims?.email;

        // Use permission-based redirect
        const redirectPath = getRedirectPath(token);
        if (redirectPath) {
          navigate(redirectPath);
        } else if (userRole === 'USER' || (tokenEmail && tokenEmail.endsWith('@corrohealth.com'))) {
          // Fallback for users without modules claim (legacy tokens)
          navigate('/user');
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        // Clear invalid token
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
      }
    }
  }, [userEmail, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (signInMethod === 'username') {
      await handleUsernameLogin();
    } else if (signInMethod === 'conferenceRoom') {
      // Conference Room authentication with email OTP
      if (!otpSent) {
        await handleSendEmailOtp();
      } else {
        await handleVerifyEmailOtp();
      }
    }
  };

  const handleUsernameLogin = async () => {
    if (formData.username && formData.password) {
      if (!captchaInput || captchaInput.trim().toUpperCase() !== captchaValue.toUpperCase()) {
        toast.error('Captcha invalid. Please try again.');
        refreshCaptcha();
        return;
      }
      try {

        const response = await authApi.emaillogin({
          username: formData.username,
          password: formData.password
        });


        if (response.status === 200 && response.data) {
          // Store the tokens in localStorage
          // Backend uses ResponseHandler.generateResponse() which returns:
          // { timestamp, status, message, data: AuthenticationResponse }
          // authApi.emaillogin returns response.data, so response here is already the outer wrapper
          // The actual auth data is in response.data (which is AuthenticationResponse)
          const authResponse = response.data; // This is the AuthenticationResponse object
          const accessToken = authResponse.accessToken;
          const refreshToken = authResponse.refreshToken;
          const requirePasswordChange = authResponse.requirePasswordChange;

          console.log('Full login response:', response);
          console.log('Auth response (AuthenticationResponse):', authResponse);
          console.log('accessToken:', accessToken ? 'present' : 'missing');
          console.log('requirePasswordChange flag:', requirePasswordChange);

          localStorage.setItem('authToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('userEmail', formData.username);

          // Check if user needs to change their password (temp password set by admin)
          if (requirePasswordChange) {
            localStorage.setItem('requirePasswordChange', 'true');
            toast.warning('You must change your temporary password before continuing.');
            navigate('/change-password?required=true');
            return;
          }

          // Extract role from token and normalize to uppercase
          const claims = getRoleFromToken(accessToken);
          console.log('JWT Claims:', claims);
          console.log('Raw role from claims:', claims?.role);
          const userRole = claims?.role?.toUpperCase();

          console.log('userrole',userRole);

          toast.success('Login successful!');

          // Navigate based on user's permissions/modules
          const redirectPath = getRedirectPath(accessToken);
          console.log('Redirect path for role:', userRole, 'modules:', claims?.modules, '-> path:', redirectPath);

          if (redirectPath) {
            navigate(redirectPath);
          } else {
            // No valid redirect path - user doesn't have required permissions
            console.log('No valid redirect path for role:', userRole);
            toast.error('You do not have permission to access the system. Please contact administrator.');
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('userEmail');
          }
        } else {
          toast.error('Login failed. Please check your credentials.');
        }
      } catch (error) {
        console.error('Error during login:', error);
        const backendMsg = error?.response?.data?.message || '';
        const statusCode = error?.response?.status || error?.response?.data?.status;
        const msgLower = String(backendMsg).toLowerCase();
        if (statusCode === 403 || msgLower.includes('inactive') || msgLower.includes('not active')) {
          toast.error('Your account is inactive. Please contact the administrator.');
        } else {
          toast.error('Login failed. Please check your credentials and try again.');
        }
      }
    } else {
      toast.warning('Please enter both username and password.');
    }
  };

  const handleSendEmailOtp = async () => {
    // Trim and validate email format
    const email = formData.email.trim();

    // Clear previous error
    setEmailError('');

    if (!email) {
      setEmailError('Email is required');
      toast.warning('Please enter your email address.');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      toast.error('Please enter a valid email address.');
      return;
    }

    // Validate @corrohealth.com domain
    if (!email.endsWith('@corrohealth.com')) {
      setEmailError('Please use your @corrohealth.com email');
      toast.error('Please use your @corrohealth.com email address.');
      return;
    }

    // Validate CAPTCHA before sending OTP
    if (!captchaInput || captchaInput.trim().toUpperCase() !== captchaValue.toUpperCase()) {
      toast.error('Captcha invalid. Please try again.');
      refreshCaptcha();
      return;
    }

    try {
      setIsLoading(true);
      console.log('Sending OTP to email:', email);
      const response = await authApi.sendEmailOtp(email);
      console.log('Email OTP API response:', response);
      console.log('Response data:', response?.data);
      setOtpSent(true);
      setOtpTimer(300); // 5 minutes
      setCanResendOtp(false);

      // Check if dev mode returned OTP in response
      // Response structure: { data: { devMode, otp, message }, message, status }
      const responseData = response?.data;
      console.log('Checking devMode:', responseData?.devMode, 'otp:', responseData?.otp);

      if (responseData?.devMode && responseData?.otp) {
        console.log('DEV MODE DETECTED - OTP:', responseData.otp);
        setDevOtp(responseData.otp);
        setShowDevOtp(true);
        toast.info(`DEV MODE - Your OTP is: ${responseData.otp}`, { autoClose: 15000 });
      } else {
        toast.success('OTP sent to your email successfully! Valid for 5 minutes.');
      }
    } catch (error) {
      console.error('Error sending email OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpSent(false);
    setCanResendOtp(false);
    setFormData(prev => ({ ...prev, otp: '' }));
    setDevOtp('');
    setShowDevOtp(false);
    await handleSendEmailOtp();
  };

  const handleShowDevOtp = async () => {
    try {
      const email = formData.email.trim();
      const response = await authApi.getDevOtp(email);
      if (response.status === 200 && response.data) {
        setDevOtp(response.data.otp);
        setShowDevOtp(true);
        toast.info(`Dev OTP: ${response.data.otp}`, { autoClose: 10000 });
      }
    } catch (error) {
      console.error('Error fetching dev OTP:', error);
      if (error?.response?.status === 403) {
        toast.error('Dev mode is not enabled on the server.');
      } else if (error?.response?.status === 404) {
        toast.error('No OTP found. Please request an OTP first.');
      } else {
        toast.error('Failed to fetch OTP from server.');
      }
    }
  };

  const handleVerifyEmailOtp = async () => {
    const email = formData.email.trim();
    const otp = formData.otp.trim();

    if (!email || !otp) {
      toast.warning('Please enter both email and OTP.');
      return;
    }

    try {
      console.log('Verifying Email OTP:', { email, otp });
      const response = await authApi.verifyEmailOtp(email, otp);
      console.log('Email OTP verification response:', response);

      if (response.status === 200 && response.data) {
        // Store the tokens in localStorage
        // Backend uses ResponseHandler which wraps data in response.data.data
        const accessToken = response.data.data?.accessToken || response.data.accessToken;
        const refreshToken = response.data.data?.refreshToken || response.data.refreshToken;

        console.log('Extracted tokens:', { accessToken: accessToken ? 'present' : 'missing', refreshToken: refreshToken ? 'present' : 'missing' });

        localStorage.setItem('authToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('userEmail', email);

        // Email OTP login is for conference room users; some tokens may not include a role claim.
        // Persist a fallback userRole so client-side checks can allow access to the user area.
        localStorage.setItem('userRole', 'USER');

        toast.success('Login successful!');

        // Conference room users always redirect to /user (same as USER role)
        navigate('/user');
      } else {
        toast.error('OTP verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying email OTP:', error);
      toast.error('Failed to verify OTP. Please check your OTP and try again.');
    }
  };

  return (
    <div className="signin-container">
      {/* Animated background shapes */}
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
        <div className="shape shape-5"></div>
      </div>

      <div className="signin-content">
        {/* Left Section - Welcome */}
        <div className="welcome-section">
          <div className="logo">
            <img
              src="/corrohealth-logo.png"
              alt="CorroHealth Logo"
              className="corrohealth-logo"
            />
          </div>

          <h1 className="welcome-title">Welcome to Corroservice your trusted in-house service hub!</h1>
          <div className="separator"></div>


        </div>

        {/* Right Section - Sign In Form */}
        <div className="signin-form-container">
          <div className="form-card">
            <h2 className="form-title">
              Sign <span className="underline">in</span>
            </h2>

            {/* Sign In Method Toggle */}
            <div className="signin-method-toggle">
              <button
                className={`toggle-btn ${signInMethod === 'username' ? 'active' : ''}`}
                onClick={() => {
                  setSignInMethod('username');
                  setOtpSent(false);
                }}
              >
                Username
              </button>
              <button
                className={`toggle-btn ${signInMethod === 'conferenceRoom' ? 'active' : ''}`}
                onClick={() => {
                  setSignInMethod('conferenceRoom');
                  setOtpSent(false);
                }}
              >
                Conference Room
              </button>
            </div>

            <form onSubmit={handleSubmit} className="signin-form">
              {signInMethod === 'username' ? (
                // Username/Password Form
                <>
                  <div className="form-group">
                    <label htmlFor="username">User Name</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder='Enter your username'
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="........."
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Conference Room Sign In Form
                <div className="conference-room-section">
                  <div className="guest-welcome">
                    <h3>Guest Conference Room Booking</h3>
                    <p>Use your @corrohealth.com email to access conference room booking</p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (emailError) setEmailError('');
                      }}
                      className={`form-input ${emailError ? 'input-error' : ''}`}
                      placeholder="your.name@corrohealth.com"
                      disabled={otpSent}
                    />
                    {emailError && <span className="error-message">{emailError}</span>}
                    {!otpSent && (
                      <small className="input-hint">
                        Enter your Corrohealth organization email
                      </small>
                    )}
                  </div>

                  {otpSent && (
                    <>
                      <div className="otp-status-card">
                        <div className="status-icon">✓</div>
                        <div className="status-text">
                          <strong>OTP Sent Successfully!</strong>
                          <p>Check your email: {formData.email}</p>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="otp">
                          Enter OTP <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="otp"
                          name="otp"
                          value={formData.otp}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Enter 6-digit OTP"
                          maxLength="6"
                          autoComplete="off"
                        />
                        <div className="otp-timer-row">
                          {otpTimer > 0 ? (
                            <span className="timer-text">
                              ⏱ OTP expires in {Math.floor(otpTimer / 60)}:{String(otpTimer % 60).padStart(2, '0')}
                            </span>
                          ) : (
                            <span className="timer-expired">OTP Expired</span>
                          )}
                          {canResendOtp && (
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              className="resend-otp-btn"
                              disabled={isLoading}
                            >
                              Resend OTP
                            </button>
                          )}
                        </div>
                        {/* Dev Mode: Show OTP Button */}
                        <div className="dev-otp-section" style={{ marginTop: '0.75rem' }}>
                          <button
                            type="button"
                            onClick={handleShowDevOtp}
                            className="show-dev-otp-btn"
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.8125rem',
                              background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: 600,
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            🔑 Show OTP (Dev Mode)
                          </button>
                          {showDevOtp && devOtp && (
                            <div style={{
                              marginTop: '0.75rem',
                              padding: '0.875rem',
                              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                              border: '2px solid #f59e0b',
                              borderRadius: '8px',
                              fontFamily: 'monospace',
                              fontSize: '1.125rem',
                              fontWeight: 700,
                              color: '#92400e',
                              textAlign: 'center',
                              letterSpacing: '0.2em'
                            }}>
                              OTP: {devOtp}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {(signInMethod === 'username' || (signInMethod === 'conferenceRoom' && !otpSent)) && (
                <div className="form-group captcha-group">
                  <label htmlFor="captcha-input">Security Check</label>
                  <CaptchaCanvas value={captchaValue} onRefresh={refreshCaptcha} />
                  <input
                    type="text"
                    id="captcha-input"
                    name="captcha"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    className="form-input captcha-input"
                    placeholder="Type the characters you see"
                    autoComplete="off"
                    inputMode="text"
                  />
                </div>
              )}

              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading && <span className="loading-spinner"></span>}
                {signInMethod === 'username'
                  ? 'Sign In'
                  : (otpSent ? 'Verify & Sign In' : 'Send OTP')
                }
              </button>
            </form>


          </div>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
};

export default SignIn;
